import { ContentCopy, Delete, Edit } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import BSTableContainer from './components/BSTableContainer';
import BSTableHeaderCell from './components/BSTableHeaderCell';
import BSTableBodyCell from './components/BSTableBodyCell';
import BSTable from './components/BSTable';

// Stored dates come back as ISO strings, Date objects or hexless Firestore
// timestamps ({ _seconds, _nanoseconds }) depending on how old the event is.
const toMomentDate = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && typeof value._seconds === 'number') {
    return moment(value._seconds * 1000 + (value._nanoseconds || 0) / 1000000);
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return moment(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

const getDateProgress = (event) => {
  const startMoment = toMomentDate(event.startDate);
  const endMoment = toMomentDate(event.endDate);

  // Without both dates there is no span to measure, so show no bar rather
  // than a misleading 0%.
  if (!startMoment || !endMoment) return null;

  const start = startMoment.toDate();
  const end = endMoment.toDate();
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  const total = end.getTime() - start.getTime();
  if (total <= 0) return 100;

  const elapsed = Date.now() - start.getTime();
  const progress = Math.round((elapsed / total) * 100);

  return Math.min(100, Math.max(0, progress));
};

const formatDateRange = (event) => {
  const start = toMomentDate(event.startDate);
  const end = toMomentDate(event.endDate);
  const startLabel = start ? start.format('MMM D, YYYY') : 'No start date';

  if (!end) {
    return `${startLabel} - No end date`;
  }

  return `${startLabel} - ${end.format('MMM D, YYYY')}`;
};

const EventDateCell = ({ event, isMobile }) => {
  const progress = getDateProgress(event);

  return (
    <Box sx={{ minWidth: isMobile ? 130 : 180 }}>
      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.primary' }}>
        {formatDateRange(event)}
      </Typography>
      {progress !== null ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.6 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              flex: 1,
              height: 5,
              borderRadius: 999,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                bgcolor: 'success.main'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {progress}%
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

const EventsTable = ({ events, onEdit, onDuplicate, onDelete, duplicateEventId }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // Absent means publish, matching how the server reads the flag for events
  // saved before this setting existed.
  const isPublished = (event) => event.publishEvent !== false;

  return (
    <BSTableContainer>
      <BSTable>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'background.paper' }}>
            <BSTableHeaderCell>Event Name</BSTableHeaderCell>
            <BSTableHeaderCell hidden={isMobile}>Format</BSTableHeaderCell>
            <BSTableHeaderCell hidden={isMobile}>Venue</BSTableHeaderCell>
            <BSTableHeaderCell hidden={isMobile}>Live Feed</BSTableHeaderCell>
            <BSTableHeaderCell>Dates</BSTableHeaderCell>
            <BSTableHeaderCell align="right">Actions</BSTableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.length === 0 ? (
            <TableRow>
              <BSTableBodyCell colSpan={isMobile ? 3 : 6} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                  No events found.
                </Typography>
              </BSTableBodyCell>
            </TableRow>
          ) : null}
          {events.map((event) => (
            <TableRow
              key={event.id}
              sx={{
                '&:last-child td, &:last-child th': { borderBottom: 0 },
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}>
              <BSTableBodyCell
                sx={{
                  fontWeight: 500,
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    color: 'text.primary'
                  }
                }}
                onClick={() => navigate(`/events/${event.id}`)}>
                {event.name}
              </BSTableBodyCell>

              <BSTableBodyCell hidden={isMobile}>
                {event.gameFormat ? event.gameFormat.name : 'N/A'}
              </BSTableBodyCell>
              <BSTableBodyCell hidden={isMobile}>
                {event.venue ? event.venue.name : 'N/A'}
              </BSTableBodyCell>

              <BSTableBodyCell hidden={isMobile}>
                <Chip
                  label={isPublished(event) ? 'Published' : 'Hidden'}
                  size="small"
                  color={isPublished(event) ? 'success' : 'default'}
                  variant="outlined"
                />
              </BSTableBodyCell>

              <BSTableBodyCell>
                <EventDateCell event={event} isMobile={isMobile} />
              </BSTableBodyCell>

              <BSTableBodyCell align="right">
                <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => onEdit(event)}
                      sx={{ color: 'text.secondary' }}
                      size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {onDuplicate && (
                    <Tooltip title="Duplicate">
                      <span>
                        <IconButton
                          onClick={() => onDuplicate(event)}
                          sx={{ color: 'text.secondary' }}
                          disabled={duplicateEventId === event.id}
                          size="small">
                          {duplicateEventId === event.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <ContentCopy fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton onClick={() => onDelete(event)} color="error" size="small">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </BSTableBodyCell>
            </TableRow>
          ))}
        </TableBody>
      </BSTable>
    </BSTableContainer>
  );
};

export default EventsTable;
